// src/components/AdmissionPopup.jsx
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { optimizeUrl } from "../utils/uploadImage";
import "./AdmissionPopup.css";

/* ───── Settings (yahan se tabdeeli karein) ───── */
const DELAY_MS = 1000;
const IMG_TIMEOUT_MS = 4000;
const STORAGE_PREFIX = "admission_popup_";
// Case-insensitive match ke liye lowercase mein likhein
const EXCLUDED_PATHS = ["/admin", "/login"];

const isExcludedPath = () => {
  const path = window.location.pathname.toLowerCase();
  return EXCLUDED_PATHS.some((p) => path.startsWith(p));
};

/* ───── Storage ───── */
const memory = new Map();

const readFlag = (key) => {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return memory.get(key) ?? null;
  }
};

const writeFlag = (key, value) => {
  memory.set(key, value);
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    /* private mode – memory fallback */
  }
};

/* ───── Version ───── */
const hash = (s) => {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
};

const versionOf = (d) => {
  const ms = d.updatedAt?.toMillis ? d.updatedAt.toMillis() : 0;
  const content = hash(
    JSON.stringify([d.title, d.text, d.imageUrl, d.buttonText, d.buttonLink])
  );
  return `${ms}-${content}`;
};

/* ───── Image preload with timeout ───── */
const preloadImage = (src) =>
  new Promise((resolve) => {
    const img = new Image();
    const timer = setTimeout(resolve, IMG_TIMEOUT_MS);
    img.onload = img.onerror = () => {
      clearTimeout(timer);
      resolve();
    };
    img.src = src;
  });

/* Internal link ke liye Link, bahar ke link ke liye <a> */
function SmartLink({ to, className, onClick, children }) {
  if (to.startsWith("/")) {
    return (
      <Link to={to} className={className} onClick={onClick}>
        {children}
      </Link>
    );
  }
  return (
    <a
      href={to}
      className={className}
      onClick={onClick}
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </a>
  );
}

export default function AdmissionPopup() {
  const [cfg, setCfg] = useState(null);
  const [open, setOpen] = useState(false);
  const closeBtnRef = useRef(null);
  const keyRef = useRef(null);

  // 1) Firestore se settings lo
  useEffect(() => {
    if (isExcludedPath()) return;

    let cancelled = false;
    const timers = [];

    (async () => {
      try {
        const snap = await getDoc(doc(db, "settings", "admissionPopup"));
        if (cancelled || !snap.exists()) return;

        const d = snap.data();
        if (!d.enabled) return;
        if (!d.imageUrl && !d.title && !d.text) return;

        const key = STORAGE_PREFIX + versionOf(d);
        const forced = new URLSearchParams(window.location.search).has("popup");
        if (!forced && readFlag(key) === "closed") return;

        if (d.imageUrl) await preloadImage(optimizeUrl(d.imageUrl, 1200));
        if (cancelled) return;

        keyRef.current = key;
        setCfg({ ...d, key });
        timers.push(
          setTimeout(() => {
            if (!cancelled) setOpen(true);
          }, DELAY_MS)
        );
      } catch (err) {
        console.error("Admission popup load error:", err);
      }
    })();

    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
    };
  }, []);

  const close = () => {
    setOpen(false);
    if (keyRef.current) writeFlag(keyRef.current, "closed");
  };

  // 2) Popup khula ho to: Esc se band, scroll lock, focus
  useEffect(() => {
    if (!open) return;

    const onKey = (e) => e.key === "Escape" && close();
    document.addEventListener("keydown", onKey);
    document.body.classList.add("adp-lock");
    closeBtnRef.current?.focus();

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.classList.remove("adp-lock");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open || !cfg) return null;

  const hasText = Boolean(cfg.title || cfg.text);
  const hasButton = Boolean(cfg.buttonText && cfg.buttonLink);
  const imageEl = cfg.imageUrl ? (
    <img
      className={"adp-image" + (hasText ? " adp-image--banner" : "")}
      decoding="async"
      src={optimizeUrl(cfg.imageUrl, 1200)}
      alt={cfg.title || "Announcement"}
    />
  ) : null;

  const popup = (
    <div
      className="adp-overlay"
      onClick={(e) => e.target === e.currentTarget && close()}
    >
      <div
        className={"adp-modal" + (hasText ? "" : " adp-modal--poster")}
        role="dialog"
        aria-modal="true"
        aria-label={cfg.title || "Announcement"}
      >
        <button
          ref={closeBtnRef}
          type="button"
          className="adp-close"
          onClick={close}
          aria-label="Close"
        >
          ×
        </button>

        {!hasText &&
          imageEl &&
          (cfg.buttonLink ? (
            <SmartLink to={cfg.buttonLink} className="adp-poster-link" onClick={close}>
              {imageEl}
            </SmartLink>
          ) : (
            imageEl
          ))}

        {hasText && (
          <>
            {imageEl || (
              <div className="adp-header">
                <span className="adp-badge">Announcement</span>
              </div>
            )}
            <div className="adp-body">
              {cfg.title && <h2>{cfg.title}</h2>}
              {cfg.text && <p className="adp-text">{cfg.text}</p>}
              <div className="adp-actions">
                {hasButton && (
                  <SmartLink
                    to={cfg.buttonLink}
                    className="adp-btn adp-btn-primary"
                    onClick={close}
                  >
                    {cfg.buttonText}
                  </SmartLink>
                )}
                <button
                  type="button"
                  className="adp-btn adp-btn-ghost"
                  onClick={close}
                >
                  Close
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );

  // Popup ko body mein render karein taake parent ka layout/stacking asar na kare
  return createPortal(popup, document.body);
}